import {
  WireCoverageRefId,
  WireCoverageType,
  WireCoverType,
} from 'scenes/common/api/quote/types';
import { Excess } from 'scenes/common/types';
import { wireCoverageId } from 'scenes/common/api/quote/marshaller/coverage';
import {
  findPremium,
  toPremium,
} from 'scenes/common/api/quote/marshaller/premium';
import {
  MotorFunnelState,
  MotorCoverType,
  InsuranceOption,
  MarketAgreedValue,
  MotorInsuranceType,
} from 'scenes/motor/types';

import {
  WireMotorQuotePayload,
  WireMotorCoverageRequest,
  WireMotorCoverageResponse,
  WireSumInsuredType,
} from '../types';
import { toWireOptions } from './options';

const wireCoverageOptions: {
  [key in MotorInsuranceType]: WireCoverType[];
} = {
  [MotorInsuranceType.Comprehensive]: [
    WireCoverType.Comprehensive,
    WireCoverType.ComprehensivePlus,
  ],
  [MotorInsuranceType.ThirdParty]: [
    WireCoverType.ThirdPartyPropertyDamage,
    WireCoverType.ThirdPartyFireAndTheft,
  ],
  [MotorInsuranceType.Unspecified]: [],
};

export const wireCoverageRefIds: {
  [key in MotorInsuranceType]: WireCoverageRefId[];
} = {
  [MotorInsuranceType.Comprehensive]: [
    WireCoverageRefId.COMP,
    WireCoverageRefId.COMPG,
  ],
  [MotorInsuranceType.ThirdParty]: [
    WireCoverageRefId.TPPD,
    WireCoverageRefId.TPFT,
  ],
  [MotorInsuranceType.Unspecified]: [],
};

export const wireCoverType: {
  [key in MotorCoverType]: WireCoverType;
} = {
  [MotorCoverType.Comprehensive]: WireCoverType.Comprehensive,
  [MotorCoverType.ComprehensivePlus]: WireCoverType.ComprehensivePlus,
  [MotorCoverType.ThirdPartyFireAndTheft]: WireCoverType.ThirdPartyFireAndTheft,
  [MotorCoverType.ThirdPartyPropertyDamage]:
    WireCoverType.ThirdPartyPropertyDamage,
  [MotorCoverType.Unspecified]: WireCoverType.Unspecified,
};

export const wireSumInsuredType: {
  [key in MarketAgreedValue]: WireSumInsuredType;
} = {
  [MarketAgreedValue.Agreed]: WireSumInsuredType.Agreed,
  [MarketAgreedValue.Market]: WireSumInsuredType.Market,
  [MarketAgreedValue.Unspecified]: WireSumInsuredType.Unspecified,
};

const toInsuranceType = (coverType: WireCoverType): MotorInsuranceType => {
  switch (coverType) {
    case WireCoverType.ComprehensivePlus:
    case WireCoverType.Comprehensive:
      return MotorInsuranceType.Comprehensive;
    case WireCoverType.ThirdPartyFireAndTheft:
    case WireCoverType.ThirdPartyPropertyDamage:
      return MotorInsuranceType.ThirdParty;
    default:
      return MotorInsuranceType.Unspecified;
  }
};

export const toMotorCoverType = (coverType: WireCoverType): MotorCoverType => {
  switch (coverType) {
    case WireCoverType.Comprehensive:
      return MotorCoverType.Comprehensive;
    case WireCoverType.ComprehensivePlus:
      return MotorCoverType.ComprehensivePlus;
    case WireCoverType.ThirdPartyFireAndTheft:
      return MotorCoverType.ThirdPartyFireAndTheft;
    case WireCoverType.ThirdPartyPropertyDamage:
      return MotorCoverType.ThirdPartyPropertyDamage;
    default:
      return MotorCoverType.Unspecified;
  }
};

export const toWireCoverages = (
  state: MotorFunnelState,
  insuranceType: MotorInsuranceType,
  extras: string[],
  excess?: Excess
): WireMotorCoverageRequest[] =>
  wireCoverageOptions[insuranceType].map(
    (coverageType): WireMotorCoverageRequest => ({
      id: wireCoverageId[coverageType],
      name: coverageType,
      type: WireCoverageType.Standard,
      selected: false,
      premiumRefIds: [''],
      adjustmentRefIds: [''],
      excessRefIds: [excess ? excess.id : ''],
      sumInsured: {
        type: WireSumInsuredType.Market,
        amount: 0,
      },
      options: toWireOptions(state, insuranceType, extras),
    })
  );

export const toInsuranceOption = (
  coverage: WireMotorCoverageResponse,
  quote: WireMotorQuotePayload
): InsuranceOption => ({
  insuranceType: toInsuranceType(coverage.name),
  coverType: toMotorCoverType(coverage.name),
  premium: toPremium(findPremium(quote.premiums, coverage.premiumRefIds)),
});

export const unmarshalInsuranceType = (quote: WireMotorQuotePayload) =>
  [
    WireCoverType.ThirdPartyFireAndTheft,
    WireCoverType.ThirdPartyPropertyDamage,
  ].includes(quote.coverType)
    ? MotorInsuranceType.ThirdParty
    : MotorInsuranceType.Comprehensive;

export const unmarshalInsuranceOptions = (quote: WireMotorQuotePayload) =>
  quote.coverages.map((coverage) => toInsuranceOption(coverage, quote));
